import {
  faChevronLeft,
  faChevronRight,
  faCompress,
  faExpand,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Box,
  Fade,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import {
  HTMLAttributes,
  MutableRefObject,
  Ref,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { SrIconButton } from "../../Inputs/Button/SrIconButton";
import useFullscreenControlState from "../MediaControl/useFullscreenControlState";
import { PdfPageField } from "./pdf-page-field";

export type PdfPagePreview = {
  page: number;
  url: string;
  width?: number;
  height?: number;
};

export type PdfPreviewProps = {
  pages: PdfPagePreview[];
  page?: number;
  onPageChange?: (page: number) => void;
  disablePageNavigation?: boolean;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  zoomOptions?: number[];
  containerRef?: MutableRefObject<HTMLDivElement | null>;
  pageContainerRef?: Ref<HTMLDivElement>;
  pageImageRef?: MutableRefObject<HTMLImageElement | null>;
  /** Fires each time the shown page's image finishes loading, a page turn included. */
  onPageImageLoad?: () => void;
  /**
   * Said in place of the page while a controlled `page` names none of `pages`.
   * Without it, or with no pages at all, the preview says it has none.
   */
  missingPageLabel?: string;
  gestureProps?: HTMLAttributes<HTMLDivElement>;
  fullScreenMode?: boolean;
  fullScreenControlBottomOffset?: number;
  onClickFullscreen?: () => void;
  onToggleFullscreenControl?: (toggle: boolean) => void;
  cursor?: string;
  children?: ReactNode;
};

const DEFAULT_ZOOM_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
const MIN_AUTO_FIT_ZOOM = 0.05;
const MAX_AUTO_FIT_ZOOM = 1;
const ZOOM_COMPARISON_THRESHOLD = 0.001;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const toPercentLabel = (value: number) => `${Math.round(value * 100)}%`;

type Size = {
  width: number;
  height: number;
};

type Spacing = {
  horizontal: number;
  vertical: number;
};

const getPageKey = ({ page, url }: PdfPagePreview) => `${page}:${url}`;

const isUsableSize = (size: Partial<Size> | undefined): size is Size =>
  typeof size?.width === "number" &&
  typeof size?.height === "number" &&
  size.width > 0 &&
  size.height > 0;

const parseCssPixelValue = (value: string | undefined) => {
  const parsed = Number.parseFloat(value ?? "");

  return Number.isFinite(parsed) ? parsed : 0;
};

const getElementPadding = (element: HTMLElement): Spacing => {
  const styles = window.getComputedStyle(element);

  return {
    horizontal: parseCssPixelValue(styles.paddingLeft) + parseCssPixelValue(styles.paddingRight),
    vertical: parseCssPixelValue(styles.paddingTop) + parseCssPixelValue(styles.paddingBottom),
  };
};

const getAvailableViewportSize = (
  viewportElement: HTMLElement,
  contentElement: HTMLElement,
): Size => {
  const padding = getElementPadding(contentElement);

  return {
    width: Math.max(1, viewportElement.clientWidth - padding.horizontal),
    height: Math.max(1, viewportElement.clientHeight - padding.vertical),
  };
};

const resolvePageSize = (
  page: PdfPagePreview | undefined,
  naturalPageSizes: Record<string, Size>,
): Size | undefined => {
  if (!page) return undefined;

  if (isUsableSize(page)) {
    return {
      width: page.width,
      height: page.height,
    };
  }

  return naturalPageSizes[getPageKey(page)];
};

const calculateFitZoom = (
  availableSize: Size | undefined,
  pageSize: Size | undefined,
): number | undefined => {
  if (!isUsableSize(availableSize) || !isUsableSize(pageSize)) {
    return undefined;
  }

  const pageAspectRatio = pageSize.height / pageSize.width;
  const heightFitZoom = availableSize.height / (availableSize.width * pageAspectRatio);

  return clamp(heightFitZoom, MIN_AUTO_FIT_ZOOM, MAX_AUTO_FIT_ZOOM);
};

const isInteractiveElement = (element: Element | null) => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element.isContentEditable) {
    return true;
  }

  if (["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(element.tagName)) {
    return true;
  }

  return !!element.closest(
    '[role="button"],[role="combobox"],[role="listbox"],[role="menu"],[role="menuitem"],[role="option"],[role="textbox"]',
  );
};

export const PdfPreview = ({
  pages,
  page,
  onPageChange,
  disablePageNavigation = false,
  zoom,
  onZoomChange,
  zoomOptions = DEFAULT_ZOOM_OPTIONS,
  containerRef,
  pageContainerRef,
  pageImageRef,
  onPageImageLoad,
  missingPageLabel,
  gestureProps,
  fullScreenMode = false,
  fullScreenControlBottomOffset = 0,
  onClickFullscreen,
  onToggleFullscreenControl,
  cursor,
  children,
}: PdfPreviewProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [viewportElement, setViewportElement] = useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const [availableSize, setAvailableSize] = useState<Size>();
  const [naturalPageSizes, setNaturalPageSizes] = useState<Record<string, Size>>({});
  const sortedPages = useMemo(
    () =>
      [...pages].sort((left, right) => left.page - right.page || left.url.localeCompare(right.url)),
    [pages],
  );
  const pageCount = sortedPages.length;
  const defaultPage = pageCount > 0 ? sortedPages[0].page : 1;
  const [internalPage, setInternalPage] = useState(defaultPage);
  const [internalZoom, setInternalZoom] = useState(1);
  const [hasManualZoom, setHasManualZoom] = useState(false);
  const [isPageFieldFocused, setIsPageFieldFocused] = useState(false);

  const {
    showControl: isFullscreenControlShownByTimer,
    toggleControl: toggleFullscreenControl,
    autoHideControl: autoHideFullscreenControl,
  } = useFullscreenControlState();
  // The auto-hide timer must not fade the controls out from under a reviewer typing a page.
  const showFullscreenControl = isFullscreenControlShownByTimer || isPageFieldFocused;

  const documentKey = useMemo(
    () =>
      sortedPages
        .map(
          ({ page: pageNumber, url, width, height }) =>
            `${pageNumber}:${url}:${width ?? ""}:${height ?? ""}`,
        )
        .join("|"),
    [sortedPages],
  );
  const previousDocumentKeyRef = useRef(documentKey);
  const previousZoomDocumentKeyRef = useRef(documentKey);

  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef) {
        containerRef.current = node;
      }

      setViewportElement((prev) => (prev === node ? prev : node));
    },
    [containerRef],
  );

  useEffect(() => {
    onToggleFullscreenControl?.(showFullscreenControl);
  }, [showFullscreenControl, onToggleFullscreenControl]);

  useEffect(() => {
    if (!viewportElement || !contentElement) return undefined;

    const updateAvailableSize = () => {
      const nextSize = getAvailableViewportSize(viewportElement, contentElement);

      setAvailableSize((prev) =>
        prev?.width === nextSize.width && prev.height === nextSize.height ? prev : nextSize,
      );
    };

    updateAvailableSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateAvailableSize);

      return () => {
        window.removeEventListener("resize", updateAvailableSize);
      };
    }

    const observer = new ResizeObserver(updateAvailableSize);
    observer.observe(viewportElement);
    observer.observe(contentElement);

    return () => {
      observer.disconnect();
    };
  }, [contentElement, viewportElement]);

  useEffect(() => {
    if (pageCount === 0) {
      setInternalPage(1);
      previousDocumentKeyRef.current = documentKey;
      return;
    }
    if (page !== undefined) {
      previousDocumentKeyRef.current = documentKey;
      return;
    }

    if (previousDocumentKeyRef.current !== documentKey) {
      setInternalPage(sortedPages[0].page);
      previousDocumentKeyRef.current = documentKey;
      return;
    }

    const firstPage = sortedPages[0].page;
    const lastPage = sortedPages[pageCount - 1].page;
    setInternalPage((prev) => clamp(prev, firstPage, lastPage));
  }, [documentKey, page, pageCount, sortedPages]);

  useEffect(() => {
    if (previousZoomDocumentKeyRef.current === documentKey) return;

    previousZoomDocumentKeyRef.current = documentKey;
    setHasManualZoom(false);
    setNaturalPageSizes({});
    if (zoom === undefined) {
      setInternalZoom(1);
    }
  }, [documentKey, zoom]);

  const effectivePage = page ?? internalPage;
  const effectiveZoom = zoom ?? internalZoom;

  const currentIndex = useMemo(
    () => sortedPages.findIndex((entry) => entry.page === effectivePage),
    [sortedPages, effectivePage],
  );

  const currentPage = currentIndex >= 0 ? sortedPages[currentIndex] : undefined;
  const currentPageSize = useMemo(
    () => resolvePageSize(currentPage, naturalPageSizes),
    [currentPage, naturalPageSizes],
  );
  const fitZoom = useMemo(
    () => calculateFitZoom(availableSize, currentPageSize),
    [availableSize, currentPageSize],
  );
  // A controlled page past this document's last page still shows its number where the
  // reviewer can type, so they can type their way back from here.
  const canTypePage = pageCount > 1;
  const shownPageNumber =
    currentPage?.page ?? (canTypePage && !disablePageNavigation ? page : undefined);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < pageCount - 1;
  const previousPageDisabled = disablePageNavigation || !canGoPrevious;
  const nextPageDisabled = disablePageNavigation || !canGoNext;
  const isZoomedIn = effectiveZoom > 1;
  const selectedZoom = useMemo(
    () =>
      zoomOptions.find((value) => Math.abs(value - effectiveZoom) < ZOOM_COMPARISON_THRESHOLD) ??
      effectiveZoom,
    [effectiveZoom, zoomOptions],
  );
  const resolvedZoomOptions = useMemo(() => {
    const hasSelectedZoom = zoomOptions.some((value) => value === selectedZoom);

    if (hasSelectedZoom) {
      return zoomOptions;
    }

    return [...zoomOptions, selectedZoom].sort((left, right) => left - right);
  }, [selectedZoom, zoomOptions]);

  useEffect(() => {
    if (zoom !== undefined || hasManualZoom || fitZoom === undefined) {
      return;
    }

    setInternalZoom((prev) =>
      Math.abs(prev - fitZoom) < ZOOM_COMPARISON_THRESHOLD ? prev : fitZoom,
    );
  }, [fitZoom, hasManualZoom, zoom]);

  const setPage = useCallback(
    (nextPage: number) => {
      if (page === undefined) {
        setInternalPage(nextPage);
      }
      onPageChange?.(nextPage);
    },
    [onPageChange, page],
  );

  const setZoom = useCallback(
    (nextZoom: number) => {
      if (zoom === undefined) {
        setInternalZoom(nextZoom);
      }
      onZoomChange?.(nextZoom);
    },
    [onZoomChange, zoom],
  );

  const handlePreviousPage = useCallback(() => {
    if (disablePageNavigation || !canGoPrevious || currentIndex < 1) return;
    setPage(sortedPages[currentIndex - 1].page);
  }, [canGoPrevious, currentIndex, disablePageNavigation, setPage, sortedPages]);

  const handleNextPage = useCallback(() => {
    if (disablePageNavigation || !canGoNext || currentIndex < 0) return;
    setPage(sortedPages[currentIndex + 1].page);
  }, [canGoNext, currentIndex, disablePageNavigation, setPage, sortedPages]);

  // A controlled page goes out as typed, because the consumer owns the page's range:
  // beside a longer document it reaches past this one's last page.
  const handlePageFieldCommit = (typedPage: number) => {
    if (disablePageNavigation || pageCount === 0) return;

    const nextPage =
      page === undefined
        ? clamp(typedPage, sortedPages[0].page, sortedPages[pageCount - 1].page)
        : typedPage;
    if (nextPage !== shownPageNumber) {
      setPage(nextPage);
    }
  };

  const handlePageFieldBlur = () => {
    setIsPageFieldFocused(false);
    if (fullScreenMode) {
      toggleFullscreenControl(true);
      autoHideFullscreenControl();
    }
  };

  const handleZoomChange = (event: SelectChangeEvent<number>) => {
    setHasManualZoom(true);
    setZoom(event.target.value as number);
  };

  const handlePageImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    onPageImageLoad?.();
    if (!currentPage) return;

    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth <= 0 || naturalHeight <= 0) return;

    const pageKey = getPageKey(currentPage);
    setNaturalPageSizes((prev) => {
      const existingSize = prev[pageKey];
      if (existingSize?.width === naturalWidth && existingSize.height === naturalHeight) {
        return prev;
      }

      return {
        ...prev,
        [pageKey]: {
          width: naturalWidth,
          height: naturalHeight,
        },
      };
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        disablePageNavigation ||
        pageCount < 2 ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        isInteractiveElement(document.activeElement)
      ) {
        return;
      }

      if (event.code === "ArrowLeft" && canGoPrevious) {
        handlePreviousPage();
        event.preventDefault();
      } else if (event.code === "ArrowRight" && canGoNext) {
        handleNextPage();
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    canGoNext,
    canGoPrevious,
    disablePageNavigation,
    handleNextPage,
    handlePreviousPage,
    pageCount,
  ]);

  const handleViewportClick = () => {
    if (fullScreenMode) {
      toggleFullscreenControl();
    }
  };

  const pageTurnButtonStyles = {
    "&.SrIconButton-default.SrIconButton-outlined": {
      height: "20px",
      minWidth: "20px",
      borderRadius: "50%",
      border: "none",
      backgroundColor: "fg.A900",
      color: "bg.A900",
      ":hover": { backgroundColor: "fg.A700" },
    },
  };

  const noPageLabel = (pageCount > 0 && missingPageLabel) || "PDF preview not available";

  const controls = (
    <Box
      sx={{
        display: { xs: "grid", sm: "flex" },
        alignItems: "center",
        gap: 1,
        justifyContent: "space-between",
        px: 2,
        py: 1.5,
        pb: fullScreenMode ? `${fullScreenControlBottomOffset + 12}px` : 1.5,
        backdropFilter: "blur(16px)",
        backgroundColor: "rgba(0, 0, 0, 0.2)",
      }}
      onTouchMove={fullScreenMode ? () => autoHideFullscreenControl() : undefined}
      onClick={fullScreenMode ? () => autoHideFullscreenControl() : undefined}
      aria-label="pdf controls"
    >
      {/* Zoom */}
      <FormControl size="small" sx={{ minWidth: 88 }}>
        <Select
          value={selectedZoom}
          onChange={handleZoomChange}
          MenuProps={{
            container: fullScreenMode ? () => rootRef.current ?? document.body : undefined,
            PaperProps: {
              sx: {
                zIndex: (theme) => theme.zIndex.modal + 1,
              },
            },
          }}
          inputProps={{
            "aria-label": "zoom level",
          }}
          sx={{
            "& .MuiSelect-select": {
              py: 0.75,
            },
          }}
        >
          {resolvedZoomOptions.map((value) => (
            <MenuItem key={value} value={value} aria-label={`zoom to ${toPercentLabel(value)}`}>
              {toPercentLabel(value)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Pages */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ justifySelf: "center" }}
      >
        {/* Previous Page */}
        <SrIconButton
          aria-label="previous page"
          size="small"
          variant="outlined"
          sx={[pageTurnButtonStyles]}
          onClick={handlePreviousPage}
          disabled={previousPageDisabled}
        >
          <FaIcon icon={faChevronLeft} />
        </SrIconButton>
        {/* Page Counter */}
        <Typography
          variant="body2"
          component="div"
          aria-label={
            pageCount > 0 && shownPageNumber !== undefined
              ? `page ${shownPageNumber} of ${pageCount}`
              : "page unavailable"
          }
          sx={{ minWidth: 64, textAlign: "center" }}
        >
          {pageCount > 0 && shownPageNumber !== undefined ? (
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              {canTypePage ? (
                <PdfPageField
                  page={shownPageNumber}
                  pageCount={pageCount}
                  disabled={disablePageNavigation}
                  onCommit={handlePageFieldCommit}
                  onFocus={() => setIsPageFieldFocused(true)}
                  onBlur={handlePageFieldBlur}
                />
              ) : (
                shownPageNumber
              )}
              <Typography
                variant="body2"
                component="span"
                sx={{ display: "inline", ml: 0.75, opacity: 0.6 }}
              >
                / {pageCount}
              </Typography>
            </Box>
          ) : (
            "- / -"
          )}
        </Typography>
        {/* Next Page */}
        <SrIconButton
          aria-label="next page"
          size="small"
          variant="outlined"
          sx={[pageTurnButtonStyles]}
          onClick={handleNextPage}
          disabled={nextPageDisabled}
        >
          <FaIcon icon={faChevronRight} />
        </SrIconButton>
      </Stack>

      {/* Full Screen Toggle */}
      {onClickFullscreen ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ justifySelf: "end" }}>
          <SrIconButton
            aria-label="full-screen"
            size="small"
            onClick={onClickFullscreen}
            sx={{ padding: 1.25 }}
          >
            <FaIcon icon={fullScreenMode ? faCompress : faExpand} />
          </SrIconButton>
        </Stack>
      ) : (
        <Box />
      )}
    </Box>
  );

  return (
    <Stack
      ref={rootRef}
      direction="column"
      sx={{
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        ref={setViewportRef}
        onClick={handleViewportClick}
        sx={{
          flex: 1,
          minHeight: 0,
          width: 1,
          position: "relative",
          overflow: "auto",
          cursor,
          touchAction: "auto",
        }}
        {...gestureProps}
      >
        <Box
          ref={setContentElement}
          sx={{
            minHeight: "100%",
            minWidth: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            boxSizing: "border-box",
            px: { xs: 2, sm: 3 },
            py: 2,
            position: "relative",
          }}
        >
          {currentPage ? (
            <Box
              ref={pageContainerRef}
              sx={{
                position: "relative",
                flexShrink: 0,
                m: "auto",
                width: `${effectiveZoom * 100}%`,
                maxWidth: isZoomedIn ? "none" : "100%",
              }}
            >
              <Box
                component="img"
                ref={pageImageRef}
                src={currentPage.url}
                alt={`PDF page ${currentPage.page}`}
                onLoad={handlePageImageLoad}
                sx={{
                  display: "block",
                  width: isZoomedIn ? "100%" : "auto",
                  height: "auto",
                  maxWidth: isZoomedIn ? "none" : "100%",
                  maxHeight: isZoomedIn ? "none" : "100%",
                }}
              />
              {children}
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              {noPageLabel}
            </Typography>
          )}
        </Box>
      </Box>
      {fullScreenMode ? (
        <Fade in={showFullscreenControl} timeout={600}>
          <Box
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              width: "100vw",
              zIndex: (theme) => theme.zIndex.appBar,
            }}
          >
            {controls}
          </Box>
        </Fade>
      ) : (
        controls
      )}
    </Stack>
  );
};
